import React, { useState } from "react";
import ProviderPrescriptions from "./ProviderPrescriptions";
import { SERVER_URL } from "../config";
import "../styles/providerProfile.css";
import useCurrentProvider from "../hooks/useCurrentProvider";

interface Provider {
  id: string;
  firstName: string;
  lastName: string;
  specialty?: string;
  licenseNumber: string;
  createdAt: string;
  user: {
    email: string;
  };
  clinic?: {
    clinicName: string;
    address: string;
    phone: string;
    region: string;
    clinicCode: string;
  };
}

interface ProviderProfileProps {
  providerData: Provider | null;
}

const ProviderProfile: React.FC<ProviderProfileProps> = ({ providerData }) => {
  // Assume useCurrentProvider returns an object with isAdmin property
  const { isAdmin } = useCurrentProvider();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: providerData?.firstName || "",
    lastName: providerData?.lastName || "",
    specialty: providerData?.specialty || "",
    licenseNumber: providerData?.licenseNumber || "",
  });

  if (!providerData) return <p>No provider profile found.</p>;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      const response = await fetch(
        `${SERVER_URL}/api/provider/${providerData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update provider profile.");
      }

      alert("Profile updated successfully!");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  return (
    <div className="provider-full-profile-container">
      <div className="provider-profile-container">
        <div className="provider-profile-info">
          <h2>
            <strong>Name:</strong> {providerData.firstName}{" "}
            {providerData.lastName}
          </h2>
          <p>
            <strong>Email:</strong> {providerData.user.email}
          </p>
          <p>
            <strong>Specialty:</strong>{" "}
            {isEditing ? (
              <input
                type="text"
                name="specialty"
                value={formData.specialty}
                onChange={handleInputChange}
              />
            ) : (
              providerData.specialty || "N/A"
            )}
          </p>
          <p>
            <strong>License Number:</strong>{" "}
            {isEditing ? (
              <input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleInputChange}
              />
            ) : (
              providerData.licenseNumber
            )}
          </p>
          <p>
            <strong>Joined On:</strong>{" "}
            {new Date(providerData.createdAt).toLocaleDateString()}
          </p>
          {isEditing && (
            <>
              <label>
                First Name:
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                />
              </label>
              <label>
                Last Name:
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
              </label>
            </>
          )}
          {!isEditing && (
            <button className="edit-button" onClick={() => setIsEditing(true)}>
              Edit Profile
            </button>
          )}
        </div>

        {providerData.clinic && (
          <div className="provider-clinic-info">
            <h2>Clinic Affiliation</h2>
            <p>
              <strong>Clinic Name:</strong> {providerData.clinic.clinicName}
            </p>
            <p>
              <strong>Address:</strong> {providerData.clinic.address}
            </p>
            <p>
              <strong>Phone:</strong> {providerData.clinic.phone}
            </p>
            <p>
              <strong>Region:</strong> {providerData.clinic.region}
            </p>
            <p>
              <strong>Clinic Code:</strong> {providerData.clinic.clinicCode}
            </p>
          </div>
        )}

        {/* Conditionally render editing controls only if the current user is not an admin */}

        {isEditing && (
          <div className="edit-modal">
            <div className="edit-modal-content">
              <h2>Edit Provider Profile</h2>
              <label>First Name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
              />

              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
              />

              <label>Specialty</label>
              <input
                type="text"
                name="specialty"
                value={formData.specialty}
                onChange={handleInputChange}
              />

              <label>License Number</label>
              <input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleInputChange}
              />

              <div className="edit-modal-actions">
                <button className="save-button" onClick={handleSave}>
                  Save
                </button>
                <button
                  className="cancel-button"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ProviderPrescriptions providerId={providerData.id} />
    </div>
  );
};

export default ProviderProfile;
